// Validación de formularios — función PURA, para que se pueda probar de verdad.
//
// Por qué vive acá y no dentro del componente: en este contenedor no hay `react`/`react-dom`
// instalados, así que un formulario solo se puede verificar leyéndolo. Sacando la decisión de
// "¿qué falta?" a una función sin JSX, la regla queda cubierta por pruebas que sí corren, y el
// componente se queda con lo único que no se puede extraer: pintarlo.
//
// 🔴 EL DEFECTO QUE ORIGINA ESTO — `'0'` es truthy. El guard del alta de inventario era:
//
//     if (!newItem.brand || !newItem.model || !newItem.unit_price_cop || !newItem.qty_available)
//
// Un precio de `'0'` pasa ese `if` sin ruido: `'0'` es una cadena no vacía. O sea que un equipo
// con **precio 0** entraba al catálogo del marketplace, y de ese número cuelgan la comisión de la
// plataforma y lo que ve el instalador. Por eso `numero` acá exige > 0 y no "no vacío".

/**
 * Campos requeridos que faltan.
 *
 * @param {Record<string, unknown>} valores  Estado del formulario.
 * @param {Array<{clave:string, etiqueta:string, tipo?:'texto'|'numero'}>} requeridos
 * @returns {Array<{clave:string, etiqueta:string}>} en el MISMO orden en que se declararon —
 *   así el mensaje al usuario sigue el orden visual del formulario y no uno arbitrario.
 */
export function camposFaltantes(valores = {}, requeridos = []) {
  const faltan = [];
  for (const req of requeridos) {
    if (!req || !req.clave) continue;
    const v = valores ? valores[req.clave] : undefined;
    const vacio = req.tipo === 'numero'
      ? !esNumeroPositivo(v)
      : req.tipo === 'cantidad'
        ? !esNumeroNoNegativo(v)
        : !tieneTexto(v);
    if (vacio) faltan.push({ clave: req.clave, etiqueta: req.etiqueta || req.clave });
  }
  return faltan;
}

/** Texto presente: no nulo y con al menos un carácter que no sea espacio. */
export function tieneTexto(v) {
  if (v === null || v === undefined) return false;
  return String(v).trim().length > 0;
}

/**
 * Número utilizable: finito y mayor que cero.
 *
 * Se rechaza el 0 a propósito (ver el encabezado) y también `NaN`, que es lo que produce
 * `Number('abc')` — un input `type=number` no impide pegar basura en todos los navegadores.
 */
export function esNumeroPositivo(v) {
  if (v === null || v === undefined || String(v).trim() === '') return false;
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
}

/** Stock: 0 es válido (ficha en tienda sin inventario). */
export function esNumeroNoNegativo(v) {
  if (v === null || v === undefined || String(v).trim() === '') return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0;
}

/** `{ clave: true }` de lo que falta — para que el input decida si se pinta en rojo. */
export function marcasDeError(faltantes = []) {
  const out = {};
  for (const f of faltantes) if (f && f.clave) out[f.clave] = true;
  return out;
}

/**
 * El mensaje que se le muestra al usuario.
 *
 * Nombra los campos en vez de decir "revisa los datos": el formulario del proveedor tiene hasta
 * diez campos y varios condicionales según el tipo de equipo, así que un mensaje genérico obliga
 * a buscar a ojo cuál quedó vacío.
 */
export function mensajeFaltantes(faltantes = []) {
  if (!faltantes.length) return '';
  const nombres = faltantes.map((f) => f.etiqueta);
  if (nombres.length === 1) return `Falta ${nombres[0]}.`;
  const ultimo = nombres[nombres.length - 1];
  return `Faltan: ${nombres.slice(0, -1).join(', ')} y ${ultimo}.`;
}
